import clsx from 'clsx';
import { MouseEvent, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { useProfileMenuItems } from 'hooks';
import { ListItemText, Menu, MenuItem, MenuProps, PopoverOrigin, styled } from 'netline-ui';
import services from 'services';
import { NavigationArea, ProfileMenuItem } from 'types';
import Divider from 'view/components/Divider/Divider';
import UserProfileMenuItem from 'view/components/UserProfileMenuItem/UserProfileMenuItem';

import styles, { userProfileMenuClasses } from './UserProfileMenu.styles';

const anchorOrigin: PopoverOrigin = {
  vertical: 'bottom',
  horizontal: 'right',
};
const transformOrigin: PopoverOrigin = {
  vertical: 'top',
  horizontal: 'right',
};
const UserProfileMenuRoot = styled(Menu, { name: userProfileMenuClasses.$name })(styles);

type UserProfileMenuProps = Omit<MenuProps, 'onClose'> & {
  onClose: (event: MouseEvent<HTMLLIElement>, reason: 'backdropClick' | 'escapeKeyDown' | 'itemSelected') => void;
};

const UserProfileMenu = ({ className, onClose, ...props }: UserProfileMenuProps) => {
  const { t } = useTranslation();

  const profileMenuItems = useProfileMenuItems(NavigationArea.Sidebar);

  const handleLogoutClick = useCallback(() => {
    services.appFrame.auth.logout();
  }, []);

  const handleMenuItemClick = useCallback(
    (menuItem: ProfileMenuItem, event: MouseEvent<HTMLLIElement>) => {
      window.postMessage(
        { source: 'userProfileMenu', type: 'menuitemclick', payload: menuItem },
        window.location.origin,
      );
      onClose(event, 'itemSelected');
    },
    [onClose],
  );

  return (
    <UserProfileMenuRoot
      className={clsx(userProfileMenuClasses.root, className)}
      onClose={onClose}
      keepMounted
      anchorOrigin={anchorOrigin}
      transformOrigin={transformOrigin}
      {...props}
    >
      {profileMenuItems.map((menuItem) => (
        <UserProfileMenuItem
          key={menuItem.id}
          className={userProfileMenuClasses.menuItem}
          menuItem={menuItem}
          onMenuItemClick={handleMenuItemClick}
        />
      ))}
      {!!profileMenuItems.length && <Divider />}
      <MenuItem onClick={handleLogoutClick} className={userProfileMenuClasses.menuItem}>
        <ListItemText>{t('menu.logout')}</ListItemText>
      </MenuItem>
    </UserProfileMenuRoot>
  );
};

export default UserProfileMenu;
